import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * A textarea that grows to fit its content — the whole value is always
 * readable and editable, never clipped behind an inner scrollbar.
 *
 * Shared by the report editor's prose fields and the comps editor's summary
 * boxes (which previously carried a reduced local copy that missed the
 * visibility re-measure and stayed collapsed after mobile tab switches).
 *
 * Pass `textareaComponent` to style it (any styled.textarea); all other
 * props flow through.
 *
 * @param {{
 *   value: string,
 *   onChange: (e: any) => void,
 *   textareaComponent?: any,
 * } & Record<string, any>} props
 */
const AutoGrowTextarea = ({ value, onChange, textareaComponent: Field = "textarea", ...rest }) => {
  const ref = useRef(/** @type {HTMLTextAreaElement | null} */ (null));
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // A hidden element measures 0 — leave it alone and let the visibility
    // observer re-measure when it appears (mobile mounts the editor rail
    // behind the Preview tab, i.e. display:none).
    if (el.offsetParent === null && el.scrollHeight === 0) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` is the trigger — re-measure whenever the text changes (hand edits and AI revisions both update it)
  useLayoutEffect(() => {
    measure();
  }, [value, measure]);
  // Re-measure when the field's box changes for reasons other than typing:
  // becoming visible (mobile tab switch), column resize, font load.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let observer = null;
    if (typeof ResizeObserver === "function") {
      observer = new ResizeObserver(() => measure());
      observer.observe(el);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [measure]);
  return <Field ref={ref} rows={1} value={value} onChange={onChange} onFocus={measure} {...rest} />;
};

export default AutoGrowTextarea;
